import { terms } from '../../language/terms';
import {
  getLabelOverrideColour,
  getUseOverrideColour,
} from './state-colour-overrides-store';

/**
 * The single source for which colour a workflow state wears, suite-wide.
 *
 * Design: documentation/engineering/state-colour-standard.md, executing D-040 option (c).
 * Three layers, resolved in order:
 *
 *   1. The PALETTE - the restricted set of colours, each authored as a light/dark pair in
 *      tokens.css (`--agwa-state-<colour>-*`). Bounded by label uniqueness, not a chosen count.
 *   2. The LABEL REGISTRY - every uniquely labelled state, keyed on a stable labelKey, each with
 *      its display word and a default colour. Same display label = same colour, by the
 *      architect's ruling of 2026-09-03: "Submitted" is one colour whether the thing submitted
 *      is a product, a certificate, a report or a document.
 *   3. The DOMAIN MAP - each state enum's values mapped onto labelKeys. This is where a future
 *      per-use override will attach (slice B persists overrides; today the code defaults ARE the
 *      effective map).
 *
 * Labels here follow the company-status-display precedent: state words are plain strings owned
 * in exactly one place (this file), composed from `terms` where a domain term already exists
 * (the membership family). The labelKey, never the display string, is what the domain map and
 * any future stored override reference - so a rename is one edit here and breaks nothing.
 *
 * An unknown value resolves to the UNMAPPED appearance rather than throwing or defaulting to a
 * real colour: the old `_statusVariant` defaulted unknowns to amber, which dressed unclassified
 * data as a caution nobody chose (D-040's motivating defect). Unmapped is visible, neutral and
 * deliberately distinct, the same philosophy as the terms system's MISSING TERM marker.
 */

/**
 * The restricted palette: a WIDE curated gamut organised in hue families, so related meanings
 * can wear related colours - a warning red beside a critical red, several blues, several
 * greens (architect's correction, 2026-09-04: the palette is a range of suitable colours, not
 * a small fixed count). Every colour is badge-suitable by construction: a deepened fill that
 * carries white text in both themes.
 *
 * Families are the picker's and the legend's grouping; the flat colour list is derived from
 * them so the two cannot disagree. Adding a colour means adding its light/dark pair to BOTH
 * blocks of tokens.css first - the node spec `state-colour-tokens.node.spec.ts` fails the
 * suite when a key listed here has no token behind it, so the two files cannot drift apart.
 * Keys are hue names, never meanings: "crimson", not "criticalRed", because the meaning is the
 * label's to choose and one colour may serve several.
 */
export const STATE_PALETTE_FAMILIES = [
  { family: 'Reds', colours: ['red', 'rose', 'crimson', 'maroon'] },
  { family: 'Oranges', colours: ['orange', 'amber', 'gold', 'rust'] },
  { family: 'Greens', colours: ['green', 'emerald', 'olive', 'forest'] },
  { family: 'Teals', colours: ['teal', 'cyan'] },
  { family: 'Blues', colours: ['blue', 'azure', 'cobalt', 'indigo', 'navy'] },
  {
    family: 'Purples',
    colours: ['purple', 'violet', 'plum', 'fuchsia', 'magenta'],
  },
  {
    family: 'Neutrals',
    colours: ['grey', 'slate', 'stone', 'charcoal', 'taupe', 'brown'],
  },
] as const;

export const STATE_PALETTE_COLOURS = STATE_PALETTE_FAMILIES.flatMap(
  (entry) => entry.colours
);

export type StatePaletteColour =
  (typeof STATE_PALETTE_FAMILIES)[number]['colours'][number];

/** A label registry entry: the word a person sees, and the colour every state wearing it gets. */
export interface StateLabelEntry {
  /** Display word, exactly as rendered. Composed from terms where a domain term exists. */
  readonly label: string;
  /** Default colour for every state carrying this label. Overridable per use in slice B. */
  readonly colour: StatePaletteColour;
}

/**
 * Every uniquely labelled state in the suite, with its default colour.
 *
 * The `satisfies` keeps keys literal for the domain map below while enforcing that EVERY entry
 * carries a colour - the architect's rule that a new unique label ships with a default in the
 * same change is a compile error here before it is a test failure anywhere else.
 *
 * Colour rationale, where it is not obvious:
 * - superseded is purple by the architect's D-088 ruling, recorded before this file existed.
 * - pendingTest is amber (evidence awaited), distinct from grey draft (person still writing).
 * - blocked is orange, not red: red is reserved for a rejection someone chose; blocked is an
 *   operating stop that gets lifted.
 * - notMember is grey, not a warning colour: not being a member is a classification, not a
 *   problem (carried over from company-status-display's variant reasoning).
 * - deleted / draftDeleted share grey with draft: they surface only in trash views, where the
 *   view itself carries the meaning.
 */
export const STATE_LABELS = {
  draft: { label: 'Draft', colour: 'grey' },
  submitted: { label: 'Submitted', colour: 'blue' },
  // resubmitted shares submitted's blue BY DEFAULT VALUE, not by identity - the
  // state-colour standard's own worked example of that mechanism ("different words for
  // the reader, same blue by default"): the two are one workflow state (the
  // Submitted/Resubmitted alias, architect 2026-09-05, D-093) and the difference is what
  // the word tells a reader, so the word alone carries it until someone recolours one on
  // the Badge Colours page.
  resubmitted: { label: 'Resubmitted', colour: 'blue' },
  underReview: { label: 'Under Review', colour: 'teal' },
  active: { label: 'Active', colour: 'green' },
  approved: { label: 'Approved', colour: 'green' },
  superseded: { label: 'Superseded', colour: 'purple' },
  rejected: { label: 'Rejected', colour: 'red' },
  expired: { label: 'Expired', colour: 'amber' },
  pendingTest: { label: 'Pending Test', colour: 'amber' },
  compliant: { label: 'Compliant', colour: 'green' },
  retired: { label: 'Retired', colour: 'slate' },
  archived: { label: 'Archived', colour: 'slate' },
  deleted: { label: 'Deleted', colour: 'grey' },
  draftDeleted: { label: 'Draft Deleted', colour: 'grey' },
  blocked: { label: 'Blocked', colour: 'orange' },
  // The soft-deactivation family (slice E sweep): both wear slate with retired/archived -
  // an operating stop is a classification, not an alarm, matching the deactivate-never-
  // destroy rule. Before the sweep these words wore warning, danger OR neutral depending
  // on which file rendered them - the exact inconsistency D-040 recorded.
  inactive: { label: 'Inactive', colour: 'slate' },
  deactivated: { label: 'Deactivated', colour: 'slate' },
  // An inactive register names its company, not itself (D-146 rule 5): the company is what the
  // reader recognises, and plain "Inactive" beside a report would read as the report's own state.
  inactiveCompany: {
    label: `Inactive ${terms.loginGroup.lower}`,
    colour: 'slate',
  },
  // Certificate markers (slice E): manual expiry shares expired's amber (same meaning
  // family); overdue is crimson, NOT rejected's red - urgency, not a person's refusal;
  // early is benign information and wears grey.
  manuallyExpired: { label: 'Manually Expired', colour: 'amber' },
  overdue: { label: 'Overdue', colour: 'crimson' },
  early: { label: 'Early', colour: 'grey' },
  member: { label: terms.member.value, colour: 'green' },
  notMember: { label: `Not a ${terms.member.lower}`, colour: 'grey' },
  formerMember: { label: `Former ${terms.member.lower}`, colour: 'slate' },
  // Classification labels (standard section 8): not workflow states, but the same shape -
  // closed code-known sets worn as chips. Certificate types keep the standards-notation
  // words certificate-utils has always shown; report types read as their AS number, with
  // Bushfire deliberately displaying AS1530 (report-type-labels.ts records why).
  as2208: { label: 'AS/NZS 2208', colour: 'charcoal' },
  as4666: { label: 'AS 4666', colour: 'taupe' },
  as2047: { label: 'AS2047', colour: 'navy' },
  as5218: { label: 'AS5218', colour: 'indigo' },
  as1530: { label: 'AS1530', colour: 'rust' },
  wers: { label: 'WERS', colour: 'cyan' },
  // Air-infiltration classification (AS 2047, derived server-side from the two
  // @75 Pa readings): a closed code-known set worn as a badge on the report.
  airLow: { label: 'Low', colour: 'green' },
  airHigh: { label: 'High', colour: 'amber' },
  airFail: { label: 'Fail', colour: 'red' },
} as const satisfies Record<string, StateLabelEntry>;

export type StateLabelKey = keyof typeof STATE_LABELS;

/** A domain in the registry: one state enum (or boolean state pair) and its display grouping. */
export interface StateDomain {
  /** Group heading on the Badge Colours settings page. */
  readonly title: string;
  /** The server enum (or field) this domain renders, so a reader can find the contract. */
  readonly source: string;
  /**
   * Enum value -> labelKey. Keys are the PascalCase names the generated model declares; lookup
   * is case-insensitive because the wire actually sends camelCase (the
   * JsonStringEnumConverter / StringEnumSchemaTransformer disagreement recorded in
   * company-status-display.ts and documentation/dod/ISSUE-324.md).
   */
  readonly states: Readonly<Record<string, StateLabelKey>>;
}

/**
 * Every state surface the suite renders through agwa-state-badge today.
 *
 * Deliberately one entry per enum even where value sets align (GdcState vs CertificateState):
 * the repository rule is that domain enums stay separate because one will change for a reason
 * the others do not share, and this map inherits that shape. New domains are added here as
 * their call sites migrate onto agwa-state-badge (the slice E sweep).
 */
export const STATE_DOMAINS = {
  certificate: {
    title: `${terms.glassAccreditation.value} Certificates`,
    source: 'CertificateState',
    states: {
      Draft: 'draft',
      Submitted: 'submitted',
      // The Submitted / Resubmitted alias reaches certificates too (D-112, 2026-09-12): a
      // certificate submitted again after rejection wears its own word, blue by default like
      // Submitted, and is recolourable on its own.
      Resubmitted: 'resubmitted',
      Active: 'active',
      Rejected: 'rejected',
      Expired: 'expired',
      DraftDeleted: 'draftDeleted',
    },
  },
  productWorkflow: {
    // D-088: the workflow field displays as "Status" (the customer's word); its states are the
    // lifecycle a person drives.
    title: `${terms.product.value} ${terms.workflowState.value}`,
    source: 'ProductState',
    states: {
      Submitted: 'submitted',
      Active: 'active',
      Retired: 'retired',
      Deleted: 'deleted',
    },
  },
  productCompliance: {
    // D-088: the evidence-derived field displays as "Compliance"; nobody sets it by hand.
    title: `${terms.product.value} ${terms.compliance.value}`,
    source: 'ProductStatus',
    states: {
      PendingTest: 'pendingTest',
      Compliant: 'compliant',
    },
  },
  productReport: {
    title: `${terms.product.value} Test Reports`,
    source: 'ProductReportState',
    states: {
      Draft: 'draft',
      Submitted: 'submitted',
      Resubmitted: 'resubmitted',
      UnderReview: 'underReview',
      Approved: 'approved',
      Rejected: 'rejected',
      Superseded: 'superseded',
      DraftDeleted: 'draftDeleted',
    },
  },
  gdc: {
    title: 'Documents (GDC)',
    source: 'GdcState',
    states: {
      Draft: 'draft',
      Submitted: 'submitted',
      Active: 'active',
      Archived: 'archived',
      Rejected: 'rejected',
    },
  },
  membership: {
    title: terms.membership.value,
    source: 'MembershipStatus',
    states: {
      Member: 'member',
      NeverMember: 'notMember',
      FormerMember: 'formerMember',
    },
  },
  companyOperating: {
    // The underlying value is a boolean; callers pass the word the label function already
    // produces ('Active' / 'Blocked') so this domain reads like every other.
    title: `${terms.loginGroup.value} Active`,
    source: 'LoginGroup.Active',
    states: {
      Active: 'active',
      Blocked: 'blocked',
    },
  },
  // Boolean-flag domains from the slice E sweep. Each follows the companyOperating
  // precedent: the flag is not an enum, so callers pass the display word as the state
  // value. One domain per concept, per the domain-enums-stay-separate rule - a site
  // going inactive and a glass product rule going inactive are different lifecycles
  // that happen to share words today.
  siteOperating: {
    title: `${terms.loginSite.value} Active`,
    source: 'Site.IsActive',
    states: {
      Active: 'active',
      Inactive: 'inactive',
    },
  },
  siteOperationType: {
    // A site's registration for one operation type; deactivation preserves history
    // (deactivate-never-destroy), so the off word is Deactivated, not Inactive.
    title: `${terms.loginSite.value} ${terms.userGroupDomain.plural}`,
    source: 'SiteOperationType registration',
    states: {
      Active: 'active',
      Deactivated: 'deactivated',
    },
  },
  staffAccess: {
    title: `${terms.loginUser.value} Access`,
    source: 'Login.Locked',
    states: {
      Active: 'active',
      Blocked: 'blocked',
    },
  },
  memberDomainScope: {
    // A member's operation-type domain scope: deactivated scopes behave as though the
    // domain were never attached, but keep their history (deactivate-never-destroy).
    title: `${terms.userGroupDomain.value} Scopes`,
    source: 'SecurityDomainScope.Active',
    states: {
      Active: 'active',
      Inactive: 'inactive',
    },
  },
  as2208Rule: {
    title: 'AS/NZS 2208 Scope Items',
    source: 'AS2208ProductRule.IsActive',
    states: {
      Active: 'active',
      Inactive: 'inactive',
    },
  },
  as4666Product: {
    title: 'AS 4666 Products',
    source: 'AS4666Product.IsActive',
    states: {
      Active: 'active',
      Inactive: 'inactive',
    },
  },
  productRegister: {
    // Whether a company's register is active, worn on everything it holds that AGWA staff can
    // still read once it is not (D-146). Only the inactive face is ever rendered; Active is
    // mapped so the Badge Colours page shows the pair and a stray call resolves rather than
    // rendering unmapped.
    title: `${terms.productRegister.value} Active`,
    source: 'ProductRegistryOwner.IsActive',
    states: {
      Active: 'active',
      Inactive: 'inactiveCompany',
    },
  },
  certificateMarker: {
    // Computed review/expiry annotations worn beside a certificate, not a persisted
    // state machine - but rendered as badges, so their colours belong to the registry.
    title: 'Certificate Markers',
    source: 'review/expiry annotations (computed)',
    states: {
      Overdue: 'overdue',
      Early: 'early',
      ManuallyExpired: 'manuallyExpired',
    },
  },
  // Classification domains (standard section 8): the registry never required an enum, only a
  // CLOSED code-known value set with stable keys - which these are. Open, admin-editable
  // sets (lookup tables) deliberately do NOT belong here; their colour lives on the row.
  certificateType: {
    title: 'Certificate Types',
    source: "certificateType ('AS2208' | 'AS4666')",
    states: {
      AS2208: 'as2208',
      AS4666: 'as4666',
    },
  },
  airInfiltration: {
    title: 'Air Infiltration Levels',
    source:
      "airInfiltrationLevel ('Low' | 'High' | 'Fail'; NotTested renders nothing)",
    states: {
      Low: 'airLow',
      High: 'airHigh',
      Fail: 'airFail',
    },
  },
  reportType: {
    title: `${terms.product.value} Report Types`,
    source: "reportType ('AS2047' | 'AS5218' | 'WERS' | 'Bushfire')",
    states: {
      AS2047: 'as2047',
      AS5218: 'as5218',
      WERS: 'wers',
      Bushfire: 'as1530',
    },
  },
} as const satisfies Record<string, StateDomain>;

export type StateDomainKey = keyof typeof STATE_DOMAINS;

/**
 * Domains of a retired module (D-175): they stay registered, because the retired module's own code
 * still resolves its badges through them, but the Badge Colours page shows none of them: its tabs
 * drop them (and a tab left empty), and domainsWearing does not count them beside a shared label. GDC is the one retired module; its states are never
 * shown anywhere a person can reach.
 */
export const RETIRED_STATE_DOMAINS: readonly StateDomainKey[] = ['gdc'];

/** What a badge needs to paint itself: a palette colour (or the unmapped look) and a word. */
export interface ResolvedStateColour {
  /** 'unmapped' renders the dashed neutral look; every other value is a palette token family. */
  readonly colour: StatePaletteColour | 'unmapped';
  /** The display word - the label entry's word, or the raw value verbatim when unmapped. */
  readonly label: string;
  /** The label the colour came from; absent exactly when unmapped. */
  readonly labelKey?: StateLabelKey;
}

/**
 * Resolve a domain's state value to its colour and word.
 *
 * Case-insensitive on the value because the wire sends camelCase while the generated model says
 * PascalCase (see StateDomain.states). An unknown value - a new server state this registry does
 * not know yet, or garbage - resolves to unmapped showing the raw value, never to a real colour.
 */
export function resolveState(
  domain: StateDomainKey,
  value: string | null | undefined
): ResolvedStateColour {
  const raw = (value ?? '').trim();
  if (!raw) return { colour: 'unmapped', label: '-' };

  const states = STATE_DOMAINS[domain].states as Record<string, StateLabelKey>;
  const matched = Object.keys(states).find(
    (key) => key.toLowerCase() === raw.toLowerCase()
  );
  if (!matched) return { colour: 'unmapped', label: raw };

  const labelKey = states[matched];
  const entry = STATE_LABELS[labelKey];
  // Stored overrides outrank code defaults, most specific first: this one use, then the
  // label everywhere. The store starts empty, so everything renders its default until the
  // fetch lands - and forever, when nothing was ever overridden.
  const colour =
    getUseOverrideColour(domain, matched) ??
    getLabelOverrideColour(labelKey) ??
    entry.colour;
  return { colour, label: entry.label, labelKey };
}

/**
 * Resolve a bare display string to its colour - for surfaces that receive a state as text with
 * no domain attached (the search dialog's status cells render whatever column value the search
 * handler sent). Matches the label word or the labelKey, case-insensitively.
 *
 * This is what replaces `_statusVariant`, and the difference is the point: that function sent
 * every unknown value to amber; this one sends it to unmapped.
 */
export function resolveByLabel(
  value: string | null | undefined
): ResolvedStateColour {
  const raw = (value ?? '').trim();
  if (!raw) return { colour: 'unmapped', label: '-' };

  const lower = raw.toLowerCase();
  const labelKey = (Object.keys(STATE_LABELS) as StateLabelKey[]).find(
    (key) =>
      key.toLowerCase() === lower ||
      STATE_LABELS[key].label.toLowerCase() === lower
  );
  if (!labelKey) return { colour: 'unmapped', label: raw };

  const entry = STATE_LABELS[labelKey];
  // Label overrides apply here too; per-use overrides cannot, because a bare string carries
  // no domain to match one against.
  const colour = getLabelOverrideColour(labelKey) ?? entry.colour;
  return { colour, label: entry.label, labelKey };
}
